import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Routes } from "../../../common/Routes";
import { inProgress, isNullOrUndefined, isNullOrUndefinedOrEmpty } from "../../../common/utils";
import { useLoadingInputOptions } from "../../../components/condition/inputFields/hooks/LoadingInputOptions";
import { createConditionTree } from "../../../components/condition/utils/createConditionTree";
import { isNullCondition } from "../../../components/condition/utils/isNullCondition";
import { isSameKeyCondition } from "../../../components/condition/utils/isSameKeyCondition";
import {
  sendUserGroupDetailsEvent,
  UserGroupDetailsAction,
} from "../../../components/matomoClient";
import { useVerifyUserGroupType } from "../hooks/VerifyUserGroupType";
import { UserGroupActions } from "../model";
import { ConditionTypes, useGetCondition } from "./common/hooks/GetCondition";
import { useGetIndividualInclusions } from "./common/hooks/GetIndividualInclusions";
import { createSteps } from "./createSteps";
import { EditActionOfSelectKeyCondition } from "./DisplayLabelsClass";
import { KeyConditionAction, KeyConditionActions } from "./model";
import {useHandleInvalidUserGroup} from "../hooks/HandleInvalidUserGroup";
import { IsFeatureEnabledOrNot } from "src/FeatureToggles/FeatureEnabledOrNot";
import { ToggleList } from "src/FeatureToggles/toggleList";
export const KeyConditionController = (
  userGroupUniqueName,
  userGroupTypeUniqueName,
  action,
  conditionId
) => {
  const isUGRevampEnabled = IsFeatureEnabledOrNot(ToggleList.UserGroupPageRevamp)
  const [currentStepState, setCurrentStepState] = useState(0);
  const [displayLabels, setDisplayLabels] = useState(undefined);
  const [steps, setSteps] = useState(createSteps(action, isUGRevampEnabled));
  const dispatch = useDispatch();

  const userGroupDetailApiProgress = useSelector((rootState) => {
    return rootState.userGroup.userGroupDetails[userGroupUniqueName]?.progress;
  });
  const userGroupDetailApiError = useSelector(
    (rootState) => rootState.userGroup.userGroupDetails[userGroupUniqueName]?.error
  );
  const userGroupDetail = useSelector(
    (rootState) => rootState.userGroup.userGroupDetails[userGroupUniqueName]?.value
  );
  const userGroupTypeApiError = useSelector((rootState) => {
    return rootState.userGroup.types.error;
  });

  useVerifyUserGroupType(userGroupTypeUniqueName);
  useHandleInvalidUserGroup(userGroupUniqueName)

  const { type: conditionType, node: conditionNode } =
    useGetCondition(userGroupUniqueName, conditionId) ?? {};

  const individualInclusions = userGroupDetail?.individualInclusionMembers;
  const currentSpecialUser = individualInclusions?.find(
    (it) => it.userId === conditionNode?.operands.attributeValueToBeMatched
  );

  const loading = inProgress(userGroupDetailApiProgress);
  const errorFetchingDetails =
    !isNullOrUndefined(userGroupDetailApiError) || !isNullOrUndefined(userGroupTypeApiError);

  const editedKeyCondition = useSelector((rootState) => {
    return rootState.keyCondition.modifiedKeyCondition;
  });
  const editedKeyConditionTree = createConditionTree(editedKeyCondition);
  const isSpecialConditionChecked = useSelector(
    (rootState) => rootState.keyCondition.isSpecialConditionChecked
  );
  const specialConditionNode = useSelector(
    (rootState) => rootState.keyCondition.specialConditionNode
  );
  const individualInclusionsList = useGetIndividualInclusions(userGroupUniqueName);
  const selectedUser = useSelector((rootState) => rootState.keyCondition.selectedUser);

  function isIndividualInclusionMember() {
    if (isNullOrUndefined(individualInclusionsList)) {
      return false;
    }
    return individualInclusionsList?.includes(selectedUser?.key);
  }

  function isConditionEditedForEditAction() {
    return (
      action === KeyConditionAction.EDIT &&
      isSameKeyCondition(conditionNode, editedKeyConditionTree)
    );
  }

  function isNextStepButtonDisabled() {
    if (action === KeyConditionAction.DELETE) {
      return false;
    } else if (isSpecialConditionChecked) {
      return (
        isNullOrUndefinedOrEmpty(specialConditionNode.reason, true) ||
        (isIndividualInclusionMember() && action !== KeyConditionAction.EDIT) ||
        conditionNode?.reason === specialConditionNode.reason
      );
    }
    return isNullCondition(editedKeyCondition) || isConditionEditedForEditAction();
  }

  const history = useHistory();
  const progressList = [];

  steps.forEach((step) => {
    progressList.push(step.label);
  });

  function isActionPerformedOnSpecialCondition() {
    return conditionType === ConditionTypes.SpecialCondition;
  }

  useEffect(() => {
    function modifyStepsDisplayLabelsBasedOnPreviousStep() {
      const stepsCopy = steps;
      const options = { isSpecialCondition: isActionPerformedOnSpecialCondition() };
      stepsCopy[currentStepState]?.setDisplayLabels(displayLabels, options);
      setSteps([...stepsCopy]);
    }

    if (!isNullOrUndefined(displayLabels)) {
      modifyStepsDisplayLabelsBasedOnPreviousStep();
    }
  }, [displayLabels]);

  function backClick() {
    if (currentStepState === 0) {
      sendUserGroupDetailsEvent(
        UserGroupDetailsAction.BackToUserGroupDetails(userGroupDetail.name),
        userGroupDetail.name
      );
      history.push(
        `${Routes.UserGroups.basePath}/${userGroupTypeUniqueName}/${userGroupUniqueName}`
      );
    } else {
      const stepState = currentStepState - 1;
      setCurrentStepState(stepState);
      sendUserGroupDetailsEvent(
        UserGroupDetailsAction.BackToUserGroupDetails(steps[stepState].heading),
        steps[stepState].heading
      );
    }
  }

  useEffect(() => {
    if (action === KeyConditionAction.EDIT && isActionPerformedOnSpecialCondition(conditionType) && !isUGRevampEnabled) {
      setDisplayLabels(new EditActionOfSelectKeyCondition());
    }
  }, [conditionType]);

  function confirmAndClick() {
    sendUserGroupEvent();
    if (!isNullOrUndefined(steps[currentStepState]?.nextStateDisplayLabels)) {
      setDisplayLabels(steps[currentStepState].nextStateDisplayLabels);
    }
    setCurrentStepState(currentStepState + 1);
  }

  function sendUserGroupEvent() {
    const PREVIEW_AND_ADD_CONDITIONS = "Preview and add conditions";
    const PREVIEW_AND_UPDATE_CONDITIONS = "Preview and update conditions";
    const CONFIRM = "Confirm";
    const ADD = "Add";
    const UPDATE_SPECIAL_CONDITION = "Update special condition";
    const UPDATE_KEY_CONDITION = "Update key condition";
    const DELETE = "Delete";
    const confirmationButtonText = steps[currentStepState]?.confirmationButtonText;
    const confirmationText = steps[currentStepState]?.confirmationText;
    if (!isNullOrUndefined(confirmationButtonText) && !isNullOrUndefined(confirmationText)) {
      if (confirmationButtonText === PREVIEW_AND_ADD_CONDITIONS && isSpecialConditionChecked) {
        sendUserGroupDetailsEvent(
          UserGroupDetailsAction.PreviewAndAddSpecialConditions,
          steps[currentStepState].confirmationButtonText
        );
      } else if (
        confirmationButtonText === PREVIEW_AND_ADD_CONDITIONS &&
        !isSpecialConditionChecked
      ) {
        sendUserGroupDetailsEvent(
          UserGroupDetailsAction.PreviewAndAddConditions,
          steps[currentStepState].confirmationButtonText
        );
      } else if (
        confirmationButtonText === PREVIEW_AND_UPDATE_CONDITIONS &&
        isSpecialConditionChecked
      ) {
        sendUserGroupDetailsEvent(
          UserGroupDetailsAction.PreviewAndUpdateSpecialConditions,
          steps[currentStepState].confirmationButtonText
        );
      } else if (
        confirmationButtonText === PREVIEW_AND_UPDATE_CONDITIONS &&
        !isSpecialConditionChecked
      ) {
        sendUserGroupDetailsEvent(
          UserGroupDetailsAction.PreviewAndUpdateConditions,
          steps[currentStepState].confirmationButtonText
        );
      } else if (
        confirmationButtonText === CONFIRM &&
        confirmationText.includes(ADD) &&
        isSpecialConditionChecked
      ) {
        sendUserGroupDetailsEvent(
          UserGroupDetailsAction.AddSpecialConditionConfirmation,
          steps[currentStepState].confirmationButtonText
        );
      } else if (
        confirmationButtonText === CONFIRM &&
        confirmationText.includes(ADD) &&
        !isSpecialConditionChecked
      ) {
        sendUserGroupDetailsEvent(
          UserGroupDetailsAction.AddConditionConfirmation,
          steps[currentStepState].confirmationButtonText
        );
      } else if (
        confirmationButtonText === CONFIRM &&
        confirmationText.includes(UPDATE_SPECIAL_CONDITION)
      ) {
        sendUserGroupDetailsEvent(
          UserGroupDetailsAction.UpdateSpecialConditionConfirmation,
          steps[currentStepState].confirmationButtonText
        );
      } else if (
        confirmationButtonText === CONFIRM &&
        confirmationText.includes(UPDATE_KEY_CONDITION)
      ) {
        sendUserGroupDetailsEvent(
          UserGroupDetailsAction.UpdateConditionConfirmation,
          steps[currentStepState].confirmationButtonText
        );
      } else if (
        confirmationButtonText === CONFIRM &&
        confirmationText.includes(DELETE) &&
        isSpecialConditionChecked
      ) {
        sendUserGroupDetailsEvent(
          UserGroupDetailsAction.DeleteSpecialConditionConfirmation,
          steps[currentStepState].confirmationButtonText
        );
      } else if (
        confirmationButtonText === CONFIRM &&
        confirmationText.includes(DELETE) &&
        !isSpecialConditionChecked
      ) {
        sendUserGroupDetailsEvent(
          UserGroupDetailsAction.DeleteConditionConfirmation,
          steps[currentStepState].confirmationButtonText
        );
      }
    }
  }

  useLoadingInputOptions(true);

  const stepProgressComponentProps = {
    steps: progressList,
    currentStep: currentStepState,
  };

  useEffect(() => {
    const isUserGroupDetailApiNotLoaded = userGroupDetailApiProgress === undefined;
    if (isUserGroupDetailApiNotLoaded) {
      dispatch(UserGroupActions.getUserGroupDetails(userGroupUniqueName));
    }
  }, [userGroupUniqueName]);

  useEffect(() => {
    if (isActionPerformedOnSpecialCondition()) {
      dispatch(KeyConditionActions.setSpecialConditionNode(conditionNode.toJSON(true)));
      dispatch(KeyConditionActions.setIsSpecialConditionChecked(true));
      setSelectedUserForSpecialCondition();
    } else if (action === KeyConditionAction.EDIT) {
      setConditionToBeEditedAsModifiedKeyCondition();
    }
    dispatch(
      KeyConditionActions.setConditionParams({
        editingConditionId: conditionId,
        conditionAction: action,
      })
    );

    function setSelectedUserForSpecialCondition() {
      if (!isNullOrUndefined(currentSpecialUser)) {
        const selectSpecialUser = {
          key: currentSpecialUser?.userId,
          fullName: currentSpecialUser?.userName,
          employeeId: currentSpecialUser?.userEmployeeId,
          email: currentSpecialUser?.userEmail,
          reason: currentSpecialUser?.reason,
        };
        dispatch(KeyConditionActions.setSelectedUser(selectSpecialUser));
      }
    }

    function setConditionToBeEditedAsModifiedKeyCondition() {
      if (!isNullCondition(conditionNode)) {
        dispatch(KeyConditionActions.setModifiedKeyCondition(conditionNode.toJSON(true)));
      }
    }
  }, [userGroupDetail, conditionId, action]);

  function resetStepsOnfeatureToggleUpdate() {
    setSteps(createSteps(action,isUGRevampEnabled))
  }

  useEffect(resetStepsOnfeatureToggleUpdate,[isUGRevampEnabled])

  return {
    setCurrentStepState,
    stepProgressComponentProps,
    backClick,
    confirmAndClick,
    isNextStepButtonDisabled: isNextStepButtonDisabled(),
    steps,
    currentStepState,
    loading,
    errorFetchingDetails,
    setDisplayLabels,
  };
};
